import { z } from "zod";
import { movementPattern, planFinding, planSplit, priorityMuscle, } from "../training/plan";
import { MAX_PRIORITY_MUSCLES } from "../training/protocols";
import { QUICK_WORKOUT_FOCI, QUICK_WORKOUT_MAX_MINUTES, QUICK_WORKOUT_MIN_MINUTES, } from "../training/quick-workout";
import { sessionReadiness } from "../training/workout";
import { agentWriteRefusal, CATALOGUE_LOCALES, MAX_DRAFT_SESSIONS, MAX_LOAD, MAX_NOTE_LENGTH, MAX_REPS, MAX_SET_DURATION_SECONDS, MAX_TARGET_SETS, } from "./data-access";
/**
 * The AI composes the plan, the engine validates (`D228`, amending `D102`,
 * `D147`, `D213`).
 *
 * `D102` kept selection the engine's: the model could ask for a protocol, a
 * split and priorities, and the engine chose every exercise. It built plans
 * blind to what the person had done — a stalled lift came back, a lift that
 * progressed was swapped for a stranger, and the trainer could only explain a
 * plan it had not written. These three tools turn it around: the model reads
 * the person's whole planning context, picks real exercises from the
 * engine's own filtered pools, and proposes a week line by line; the engine
 * REFUSES what it must (contraindicated, excluded, unequipped, too hard,
 * a family twice in a day, an unknown id), CLAMPS what it may (the protocol's
 * rep range, the set ramp, the session ceiling, rest, a start load above the
 * last load lifted) and reports both, with the evidence ids of every rule.
 *
 * ── THE SAME RULE AS EVERY SCHEMA ON THIS CHANNEL (`D72`) ───────────────────
 * No field names a person or a plan: the account is the `AgentActor`.
 */
/** `getPlanningContext` takes the locale names come back in, and nothing else. */
export const getPlanningContextRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
});
/**
 * `composeQuickWorkout` (`D249`): the app's "Treino rápido" engine, asked by
 * the trainer — the same function, the same session for the same answers.
 * A READ: nothing is written, so it needs no write ledger; the person starts
 * it from Today, where the same request composes the same session. No gym
 * id: the trainer composes from the equipment in effect, and saved gyms are
 * the person's own (`D202`). The muscle is a word, as `findPatternCandidates`
 * takes it, never a catalogue id.
 */
export const composeQuickWorkoutRequest = z
    .strictObject({
    focus: z
        .enum(QUICK_WORKOUT_FOCI)
        .describe("full_body (default), upper, lower, or muscle with `muscle`")
        .optional(),
    locale: z.enum(CATALOGUE_LOCALES).optional(),
    minutes: z
        .int()
        .min(QUICK_WORKOUT_MIN_MINUTES)
        .max(QUICK_WORKOUT_MAX_MINUTES)
        .describe("The minutes the person has, warm-up included"),
    muscle: z
        .string()
        .min(1)
        .max(40)
        .describe("With focus: muscle only — its name, English or Portuguese")
        .optional(),
    readiness: sessionReadiness
        .describe("Only when the person told you both last night's sleep and today's stress")
        .optional(),
})
    .superRefine((value, context) => {
    if ((value.focus === "muscle") !== (value.muscle !== undefined)) {
        context.addIssue({
            code: "custom",
            message: "a muscle focus names its muscle, and only it does",
            path: ["muscle"],
        });
    }
});
/** How many candidates one `findPatternCandidates` call may return. */
export const MAX_PATTERN_CANDIDATES = 20;
export const findPatternCandidatesRequest = z
    .strictObject({
    equipmentOnly: z
        .boolean()
        .describe("Default true: only what the person's equipment can load. False widens to every equipment, still filtered by injuries, exclusions and level.")
        .optional(),
    limit: z.int().min(1).max(MAX_PATTERN_CANDIDATES).optional(),
    locale: z.enum(CATALOGUE_LOCALES).optional(),
    muscle: z
        .string()
        .trim()
        .min(1)
        .max(60)
        .describe('A muscle in English or Portuguese ("glutes", "Peitoral").')
        .optional(),
    pattern: movementPattern.optional(),
})
    .refine((value) => value.pattern !== undefined || value.muscle !== undefined, {
    message: "name a pattern or a muscle",
});
/** Why a composed line was picked — rendered by the app (`D147`). */
export const COMPOSE_REASON_KEYS = [
    "goal",
    "priority",
    "progressing",
    "replaces_stalled",
    "replaces_painful",
    "variety",
];
/** A day holds at most this many lines; a week at most six days. */
export const MAX_COMPOSE_ITEMS_PER_DAY = 10;
export const composeLine = z.strictObject({
    exerciseId: z.string().min(1).max(64),
    reasonKey: z.enum(COMPOSE_REASON_KEYS),
    repsMax: z.int().min(1).max(MAX_REPS).optional(),
    repsMin: z.int().min(1).max(MAX_REPS).optional(),
    restSeconds: z.int().min(0).max(600).optional(),
    seconds: z.int().min(1).max(MAX_SET_DURATION_SECONDS).optional(),
    sets: z.int().min(1).max(MAX_TARGET_SETS).optional(),
    startLoadKg: z.number().min(0).max(MAX_LOAD).optional(),
    /** One short line, in the person's language: why THIS exercise for them. */
    why: z.string().trim().min(1).max(MAX_NOTE_LENGTH).optional(),
});
export const composePlanDraftRequest = z.strictObject({
    days: z
        .array(z.strictObject({
        items: z.array(composeLine).min(1).max(MAX_COMPOSE_ITEMS_PER_DAY),
        label: z.string().trim().min(1).max(40),
    }))
        .min(1)
        .max(MAX_DRAFT_SESSIONS),
    /** `true` validates and reports, and writes NOTHING. */
    dryRun: z.boolean().optional(),
    priorityMuscles: z.array(priorityMuscle).max(MAX_PRIORITY_MUSCLES).optional(),
    protocolId: z.string().min(1).max(64).optional(),
    /** The week's shape, for the record; omitted is `custom`. */
    split: planSplit.optional(),
});
export const composeRefusal = z.object({
    day: z.int(),
    exerciseId: z.string(),
    line: z.int(),
    reason: z.string(),
});
export const composeAdjustment = z.object({
    day: z.int(),
    exerciseId: z.string(),
    field: z.string(),
    from: z.number().nullable(),
    line: z.int(),
    rule: z.string(),
    to: z.number().nullable(),
});
export const agentComposePlanResult = z.discriminatedUnion("ok", [
    z.object({
        adjustments: z.array(composeAdjustment),
        /** The draft written, or `null` on a dry run or with refusals. */
        draftId: z.string().nullable(),
        /** Every rule applied cites one of these (`D19`). */
        evidenceIds: z.array(z.string()),
        notices: z.array(planFinding),
        ok: z.literal(true),
        protocolId: z.string(),
        refusals: z.array(composeRefusal),
        sessions: z
            .array(z.object({
            focus: z.array(movementPattern),
            label: z.string(),
            sessionId: z.string(),
        }))
            .max(MAX_DRAFT_SESSIONS),
        written: z.boolean(),
    }),
    agentWriteRefusal,
]);
export const agentPatternCandidatesResult = z.object({
    candidates: z.array(z.object({
        activationPct: z.number(),
        equipment: z.string(),
        exerciseId: z.string(),
        family: z.string().nullable(),
        lastDoneAt: z.string().nullable(),
        lastLoadKg: z.number().nullable(),
        mechanic: z.string().nullable(),
        name: z.string(),
        pattern: movementPattern.nullable(),
        primaryMuscle: z.string(),
    })),
    /** A muscle name nothing in the catalogue matched. */
    unmatched: z.array(z.string()),
});
//# sourceMappingURL=planning-tools.js.map